import React, { useEffect } from 'react';
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc';
import type { StreamingSession } from '@youga/youga-interfaces';

interface Props {
  session: StreamingSession;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  flipped: boolean;
  onConnected: (connected: boolean) => void;
}

const KinesisRemoteVideo: React.FC<Props> = ({
  session,
  videoRef,
  flipped,
  onConnected,
}) => {
  useEffect(() => {
    const {
      channelARN,
      channelEndpoint,
      viewerClientId,
      viewerSignalingUrl,
      region,
      iceServers,
    } = session;
    if (
      !channelARN ||
      !channelEndpoint ||
      !viewerClientId ||
      !viewerSignalingUrl ||
      !region
    ) {
      onConnected(false);
      return undefined;
    }

    let active = true;
    const peerConnection = new RTCPeerConnection({ iceServers });
    const signalingClient = new SignalingClient({
      channelARN,
      channelEndpoint,
      clientId: viewerClientId,
      region,
      role: Role.VIEWER,
      requestSigner: {
        getSignedURL: async () => viewerSignalingUrl,
      },
    });

    peerConnection.addTransceiver('video', { direction: 'recvonly' });
    peerConnection.onicecandidate = ({ candidate }) => {
      if (candidate) signalingClient.sendIceCandidate(candidate);
    };
    peerConnection.ontrack = ({ streams }) => {
      const [remoteStream] = streams;
      if (videoRef.current && remoteStream) {
        videoRef.current.srcObject = remoteStream;
        void videoRef.current.play().catch(() => undefined);
      }
    };
    peerConnection.onconnectionstatechange = () => {
      if (!active) return;
      onConnected(peerConnection.connectionState === 'connected');
    };
    signalingClient.on('open', async () => {
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: false,
        offerToReceiveVideo: true,
      });
      await peerConnection.setLocalDescription(offer);
      signalingClient.sendSdpOffer(offer as RTCSessionDescription);
    });
    signalingClient.on('sdpAnswer', async (answer) => {
      await peerConnection.setRemoteDescription(answer);
    });
    signalingClient.on('iceCandidate', async (candidate) => {
      await peerConnection.addIceCandidate(candidate);
    });
    signalingClient.on('error', () => onConnected(false));
    signalingClient.on('close', () => onConnected(false));
    signalingClient.open();

    return () => {
      active = false;
      signalingClient.close();
      peerConnection.close();
      if (videoRef.current?.srcObject instanceof MediaStream) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
      onConnected(false);
    };
  }, [onConnected, session, videoRef]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 20,
        objectFit: 'contain',
        transform: flipped ? 'scaleX(-1)' : undefined,
      }}
    />
  );
};

export default KinesisRemoteVideo;
